import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { PasswordTokenHandler } from 'components';
import {
  checkSsoAzureConfig,
  checkSsoJwtConfig,
  checkSsoSamlConfig,
} from 'actions/app.actions';
import {
  ssoAzureSelector,
  ssoJwtSelector,
  ssoSamlSelector,
  loginLoadingSelector,
} from 'reducers/app.reducer';
import ForgotPasswordForm from './ForgotPasswordForm';
import ResetPasswordForm from './ResetPasswordForm';
import { ICustomTheme } from 'interfaces';
import { TitleWithUIGuidelines } from 'component-library/atoms/TitleWithUIGuidelines';
import { BasicTextWithUIGuidelines } from 'component-library/atoms/BasicTextWithUIGuidelines';

const Container = styled.div(({ theme }: { theme: ICustomTheme }) => ({
  marginTop: theme.spacing(4),
}));

const ResetPassword = (): JSX.Element => {
  const loading = useSelector(loginLoadingSelector);
  const { t } = useTranslation();
  const hasSsoAzureConfigured = useSelector(ssoAzureSelector);
  const hasSsoJwtConfigured = useSelector(ssoJwtSelector);
  const hasSsoSamlConfigured = useSelector(ssoSamlSelector);
  const dispatch = useDispatch();

  const hasSSO =
    hasSsoAzureConfigured || hasSsoJwtConfigured || hasSsoSamlConfigured;

  useEffect(() => {
    dispatch(checkSsoAzureConfig.request());
    dispatch(checkSsoJwtConfig.request());
    dispatch(checkSsoSamlConfig.request());
  }, [dispatch]);

  return (
    <PasswordTokenHandler>
      {(token) => (
        <Container>
          <TitleWithUIGuidelines title={t('auth.reset_password.title')} />
          {hasSSO && (
            <BasicTextWithUIGuidelines
              text={t('auth.reset_password.ssoReset')}
            />
          )}
          {token ? (
            <ResetPasswordForm loading={loading} token={token} />
          ) : (
            <ForgotPasswordForm loading={loading} />
          )}
        </Container>
      )}
    </PasswordTokenHandler>
  );
};

export default ResetPassword;
